import type { Document } from "../document/Document";
import type { Layer, ShapeLayer } from "../document/Layer";
import type { PathModel } from "../document/PathModel";
import type { Command } from "./Command";

// EditPathCommand — replaces a ShapeLayer's path with a new one.
// Used by DirectSelectionTool to make anchor drags and deletions undoable.
// Stores explicit before/after PathModel snapshots so invert is exact.

export class EditPathCommand implements Command {
  readonly kind = "edit-path";

  constructor(
    private readonly layerId: string,
    private readonly pathBefore: PathModel,
    private readonly pathAfter: PathModel,
  ) {}

  apply(doc: Document): Document {
    return { ...doc, layers: setPath(doc.layers, this.layerId, this.pathAfter) };
  }

  invert(_before: Document, _after: Document): Command {
    return new EditPathCommand(this.layerId, this.pathAfter, this.pathBefore);
  }
}

function setPath(layers: readonly Layer[], id: string, path: PathModel): Layer[] {
  return layers.map((l) => {
    if (l.id === id && l.kind === "shape") {
      const shape = l as ShapeLayer;
      return { ...shape, path };
    }
    if (l.kind === "group") return { ...l, children: setPath(l.children, id, path) };
    return l;
  });
}

/** Deep-clone a PathModel so mutations to one copy don't affect the other. */
export function clonePathModel(path: PathModel): PathModel {
  return {
    subpaths: path.subpaths.map((sp) => ({
      ...sp,
      points: sp.points.map((pt) => ({
        ...pt,
        ...(pt.leftDirection ? { leftDirection: { ...pt.leftDirection } } : {}),
        ...(pt.rightDirection ? { rightDirection: { ...pt.rightDirection } } : {}),
      })),
    })),
    ...(path.transform ? { transform: { ...path.transform } } : {}),
  };
}
